import { addHours, addMinutes, addSeconds, format } from 'date-fns';

export type AuditExportScope = 'dateRange' | 'fullHistory';
export type DateRangeExportFormat = 'csv' | 'pdf';

export const AUDIT_TRAIL_EXPORTED_EVENT = 'audit-trail-exported';

/** Segment payload for an Audit Trail CSV download or email enqueue (success or failure). */
export function auditTrailExportedEvent(scope: AuditExportScope, format: DateRangeExportFormat, succeeded: boolean) {
  return {
    name: AUDIT_TRAIL_EXPORTED_EVENT,
    data: { scope, format, succeeded },
  };
}

export const CSV_FORMAT_TOOLTIP = 'An editable CSV of the selected range\nwill download instantly.';

export const CERTIFIED_PDF_FORMAT_TOOLTIP = "A locked, uneditable PDF will be sent\nto your email once it's ready.";

export const EXPORT_IN_PROGRESS_TOOLTIP = "Your export is already in progress. We'll email you when it's ready.";

export const SELECT_DATE_RANGE_TOOLTIP = 'Select a date range in order to generate a report';

export function isEmailExport(scope: AuditExportScope, format: DateRangeExportFormat): boolean {
  return scope === 'fullHistory' || format === 'pdf';
}

export function primaryCtaLabel(scope: AuditExportScope, format: DateRangeExportFormat): 'Download' | 'Begin Export' {
  return isEmailExport(scope, format) ? 'Begin Export' : 'Download';
}

type EnqueueError = { response?: { status?: number } } | null | undefined;

/** Keep the CTA latched after settle on 202 (no error) or 409; 429/503/other must be retryable. */
export function shouldReleaseExportLatch(error: EnqueueError): boolean {
  if (!error) return false;
  return error.response?.status !== 409;
}

/**
 * Only the mutation that just finished may change the shared latch. Leftover errors from the
 * other export type must not unlatch a 202/409 or override a fresh 409.
 */
export function resolveExportLatchAfterMutations(input: {
  csvJustSettled: boolean;
  pdfJustSettled: boolean;
  csvError: EnqueueError;
  pdfError: EnqueueError;
}): 'keep' | 'release' | 'unchanged' {
  const { csvJustSettled, pdfJustSettled, csvError, pdfError } = input;
  if (!csvJustSettled && !pdfJustSettled) return 'unchanged';

  const keep =
    (csvJustSettled && !shouldReleaseExportLatch(csvError)) || (pdfJustSettled && !shouldReleaseExportLatch(pdfError));
  if (keep) return 'keep';
  return 'release';
}

export function ctaTooltip(input: {
  scope: AuditExportScope;
  format: DateRangeExportFormat;
  exportRequested: boolean;
  datesIncomplete: boolean;
}): string {
  const { scope, format, exportRequested, datesIncomplete } = input;
  if (exportRequested && isEmailExport(scope, format)) return EXPORT_IN_PROGRESS_TOOLTIP;
  if (scope === 'dateRange' && datesIncomplete) return SELECT_DATE_RANGE_TOOLTIP;
  return '';
}

/** Inclusive local day bounds used by both CSV download and locked-PDF enqueue. */
export function toAuditTrailExportRange(
  start: Date,
  end: Date,
): {
  utcSince: string;
  utcUntil: string;
  sinceDate: string;
  untilDate: string;
} {
  const localUntil = addSeconds(addMinutes(addHours(end, 23), 59), 59);
  return {
    utcSince: start.toUTCString(),
    utcUntil: localUntil.toUTCString(),
    sinceDate: format(start, 'yyyy-MM-dd'),
    untilDate: format(end, 'yyyy-MM-dd'),
  };
}
